/* eslint-disable jsx-a11y/label-has-associated-control */
import { Calendar } from "components/ui/Calendar";
import classNames from "components/ui/classNames";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";
import addMonths from "date-fns/addMonths";
import useSelfie from "lib/hooks/useSelfie";
import type { IAgendaQuery } from "lib/queries/__generated__/AgendaQuery.generated";
import { startTransition, useEffect, useId, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IIssuerType } from "types/graphqlTypes";
import TwoPassRender from "components/shared/TwoPassRender";
import CalendarNotes from "./CalendarNotes";
import { EventsCalendarFilterRange, EventsCalenderFilterEventType } from "./enums";
import { getFilterValuesBySelectOption } from "./utils";

export enum RightSideFiltersEnum {
  MyPositions = "MY_POSITIONS",
  IsInBookmarks = "IS_IN_BOOKMARKS",
  isInSP500 = "IS_IN_SP500",
  EventType = "EVENT_TYPE",
  FilterRange = "FILTER_RANGE",
  MonthViewCalendar = "MONTH_VIEW_CALENDAR",
}

interface RightSideFiltersProps {
  myPositions: boolean;
  isInSP500: boolean;
  isInBookmarks: boolean;
  eventType: EventsCalenderFilterEventType | null;
  issuerType: IIssuerType | null;
  period: EventsCalendarFilterRange;
  calendarInterval: {
    startDate: Date;
    endDate: Date;
  };
  enableFilters: RightSideFiltersEnum[];
  className?: string;
  eventsCalendar?: IAgendaQuery;
  eventTypeOptions?: EventsCalenderFilterEventType[];

  setMyPositions: (value: boolean) => void;
  setIsInSP500: (value: boolean) => void;
  setIsInBookmarks: (value: boolean) => void;
  setEventType: (value: EventsCalenderFilterEventType | null) => void;
  setPeriod: (value: EventsCalendarFilterRange) => void;
  setCalendarInterval: (value: { startDate: Date; endDate: Date }) => void;
}

export default function RightSideFilters({
  myPositions,
  isInSP500,
  isInBookmarks,
  eventType,
  issuerType,
  period,
  calendarInterval,
  enableFilters,
  className,
  eventsCalendar,
  eventTypeOptions,
  setMyPositions,
  setIsInSP500,
  setIsInBookmarks,
  setEventType,
  setPeriod,
  setCalendarInterval,
}: RightSideFiltersProps) {
  const intl = useIntl();
  const selfie = useSelfie();
  const myPositionsCheckboxId = useId();
  const isInSP500CheckboxId = useId();
  const isInBookmarksCheckboxId = useId();
  const [month, setMonth] = useState<Date>(calendarInterval.startDate);
  const enabledEventTypeOptions = eventTypeOptions ?? [
    EventsCalenderFilterEventType.Earnings,
    EventsCalenderFilterEventType.Dividends,
    EventsCalenderFilterEventType.StockDividends,
    EventsCalenderFilterEventType.RightsIssue,
    EventsCalenderFilterEventType.StockSplit,
    EventsCalenderFilterEventType.SpinOff,
  ];

  useEffect(() => {
    setMonth(calendarInterval.startDate);
  }, [calendarInterval.startDate]);

  function handleSetPeriod(selectedOption: EventsCalendarFilterRange) {
    const { filterType, startDate, endDate } = getFilterValuesBySelectOption(selectedOption);

    startTransition(() => {
      setMonth(startDate);
      setPeriod(filterType);
      setCalendarInterval({
        startDate,
        endDate,
      });
    });
  }

  return (
    <div className={classNames("grid grid-cols-1 gap-4 sm:sticky sm:top-24", className)}>
      {enableFilters.includes(RightSideFiltersEnum.MyPositions) && selfie && (
        <div
          aria-checked={myPositions}
          className={classNames({
            "items-center rounded-md border bg-white p-4 tracking-wide shadow dark:border-slate-700 dark:bg-slate-800 dark:shadow-none":
              true,
            "border-violet-300 dark:border-violet-600": myPositions,
            "border-transparent dark:border-slate-700": !myPositions,
          })}
        >
          <div className="flex select-none items-center gap-x-2">
            <input
              id={myPositionsCheckboxId}
              type="checkbox"
              name="Minha Carteira"
              className={classNames(
                "h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600",
                "focus:ring-violet-500",
                "dark:border-slate-600 dark:bg-slate-700",
                "dark:checked:border-violet-600 dark:checked:bg-violet-600",
              )}
              checked={myPositions}
              onChange={() => setMyPositions(!myPositions)}
            />

            <label
              className={classNames("cursor-pointer text-sm tracking-wider", {
                "font-bold text-violet-500 dark:text-violet-400": myPositions,
                "font-medium text-gray-500 dark:text-slate-400": !myPositions,
              })}
              htmlFor={myPositionsCheckboxId}
            >
              <FormattedMessage defaultMessage="Minha Carteira" id="6jfnJ3" />
            </label>
          </div>
        </div>
      )}

      {enableFilters.includes(RightSideFiltersEnum.IsInBookmarks) && selfie && (
        <div
          aria-checked={isInBookmarks}
          className={classNames({
            "items-center rounded-md border bg-white p-4 tracking-wide shadow dark:border-slate-700 dark:bg-slate-800 dark:shadow-none":
              true,
            "border-violet-300 dark:border-violet-600": isInBookmarks,
            "border-transparent dark:border-slate-700": !isInBookmarks,
          })}
        >
          <div className="flex select-none items-center gap-x-2">
            <input
              id={isInBookmarksCheckboxId}
              type="checkbox"
              name="Minha Carteira"
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600 focus:ring-violet-500 dark:border-slate-600 dark:bg-slate-700 dark:checked:border-violet-600 dark:checked:bg-violet-600"
              checked={isInBookmarks}
              onChange={() => setIsInBookmarks(!isInBookmarks)}
            />

            <label
              className={classNames("cursor-pointer text-sm tracking-wider", {
                "font-bold text-violet-500 dark:text-violet-400": isInBookmarks,
                "font-medium text-gray-500 dark:text-slate-400": !isInBookmarks,
              })}
              htmlFor={isInBookmarksCheckboxId}
            >
              <FormattedMessage defaultMessage="Favoritos" id="g2IZZj" />
            </label>
          </div>
        </div>
      )}

      {enableFilters.includes(RightSideFiltersEnum.isInSP500) && issuerType !== IIssuerType.BrazilianCompany && (
        <div
          aria-checked={isInSP500}
          className={classNames({
            "space-y-2 rounded-md border bg-white p-4 tracking-wide shadow dark:border-slate-700 dark:bg-slate-800 dark:shadow-none":
              true,
            "border-violet-300 dark:border-violet-600": isInSP500,
            "border-transparent dark:border-slate-700": !isInSP500,
          })}
        >
          <div className="flex select-none items-center space-x-2">
            <input
              id={isInSP500CheckboxId}
              type="checkbox"
              name="SEP500"
              className="h-4 w-4 cursor-pointer rounded border-gray-300 text-violet-600 focus:ring-violet-500 dark:border-slate-600 dark:bg-slate-700 dark:checked:border-violet-600 dark:checked:bg-violet-600"
              checked={isInSP500}
              onChange={() => setIsInSP500(!isInSP500)}
            />

            <label
              className={classNames("cursor-pointer text-sm tracking-wider", {
                "font-bold text-violet-500 dark:text-violet-400": isInSP500,
                "font-medium text-gray-500 dark:text-slate-400": !isInSP500,
              })}
              htmlFor={isInSP500CheckboxId}
            >
              <FormattedMessage defaultMessage="S&P 500" id="p9EKzD" />
            </label>
          </div>
        </div>
      )}

      {enableFilters.includes(RightSideFiltersEnum.EventType) && (
        <Select
          value={eventType || EventsCalenderFilterEventType.All}
          onValueChange={(value) => setEventType(value as EventsCalenderFilterEventType)}
        >
          <SelectTrigger
            className={classNames(
              "font-aeonik",
              "h-12 w-full rounded-lg",
              "border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800",
              "text-sm font-medium tracking-wide text-slate-600 dark:text-slate-300",
              "disabled:select-none",
              "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300 dark:hover:ring-slate-700 dark:hover:ring-offset-slate-800",
              "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300 dark:focus-visible:ring-indigo-900/50 dark:focus-visible:ring-offset-slate-800",
              "truncate",
            )}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                defaultMessage: "Filtre o tipo do evento",
                id: "puERTR",
              })}
            />
          </SelectTrigger>

          <SelectContent
            className="rounded-lg border border-slate-200 text-sm dark:border-slate-700 dark:bg-slate-800"
            side="top"
          >
            <SelectItem value={EventsCalenderFilterEventType.All}>
              <FormattedMessage defaultMessage="Todos" id="UA87I4" description="Eventos Corporativos" />
            </SelectItem>
            {enabledEventTypeOptions.includes(EventsCalenderFilterEventType.Earnings) && (
              <SelectItem value={EventsCalenderFilterEventType.Earnings}>
                <FormattedMessage defaultMessage="Balanços" id="k+Oqnk" description="Eventos Corporativos" />
              </SelectItem>
            )}
            {enabledEventTypeOptions.includes(EventsCalenderFilterEventType.Dividends) && (
              <SelectItem value={EventsCalenderFilterEventType.Dividends}>
                <FormattedMessage defaultMessage="Proventos" id="4UvohG" description="Eventos Corporativos" />
              </SelectItem>
            )}
            {enabledEventTypeOptions.includes(EventsCalenderFilterEventType.StockDividends) && (
              <SelectItem value={EventsCalenderFilterEventType.StockDividends}>
                <FormattedMessage defaultMessage="Bonificação" id="xEQI3s" description="Eventos Corporativos" />
              </SelectItem>
            )}
            {enabledEventTypeOptions.includes(EventsCalenderFilterEventType.RightsIssue) && (
              <SelectItem value={EventsCalenderFilterEventType.RightsIssue}>
                <FormattedMessage defaultMessage="Subscrição" id="5WMOhr" description="Eventos Corporativos" />
              </SelectItem>
            )}
            {enabledEventTypeOptions.includes(EventsCalenderFilterEventType.StockSplit) && (
              <SelectItem value={EventsCalenderFilterEventType.StockSplit}>
                <FormattedMessage defaultMessage="Desdobramento" id="SYkm7W" description="Eventos Corporativos" />
              </SelectItem>
            )}
            {enabledEventTypeOptions.includes(EventsCalenderFilterEventType.SpinOff) && (
              <SelectItem value={EventsCalenderFilterEventType.SpinOff}>
                <FormattedMessage defaultMessage="Cisão" id="xDlFg4" description="Eventos Corporativos" />
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      )}

      {enableFilters.includes(RightSideFiltersEnum.FilterRange) && (
        <Select
          value={period}
          onValueChange={(value) => {
            handleSetPeriod(value as EventsCalendarFilterRange);
          }}
        >
          <SelectTrigger
            className={classNames(
              "font-aeonik",
              "h-12 w-full rounded-lg",
              "border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-800",
              "text-sm font-medium tracking-wide text-slate-600 dark:text-slate-300",
              "disabled:select-none",
              "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300 dark:hover:ring-slate-700 dark:hover:ring-offset-slate-800",
              "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300 dark:focus-visible:ring-indigo-900/50 dark:focus-visible:ring-offset-slate-800",
              "truncate",
            )}
          >
            <SelectValue
              placeholder={intl.formatMessage({
                defaultMessage: "Filtre o período do evento",
                id: "HY8UP3",
              })}
            />
          </SelectTrigger>

          <SelectContent
            className="rounded-lg border border-slate-200 text-sm dark:border-slate-700 dark:bg-slate-800"
            side="top"
          >
            <SelectItem value={EventsCalendarFilterRange.Today}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="Hoje ({count})"
                id="BY2iNs"
                values={{
                  count: eventsCalendar?.agenda?.breakdown?.today || 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheWeek}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="Na Semana ({count})"
                id="bT68E6"
                values={{
                  count: eventsCalendar?.agenda?.breakdown?.inTheWeek || 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheMonth}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="No Mês ({count})"
                id="lWqHok"
                values={{
                  count: eventsCalendar?.agenda?.breakdown?.inTheMonth || 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheLastMonth}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="No Último Mês ({count})"
                id="Kta/aU"
                values={{
                  count: eventsCalendar?.agenda?.breakdown?.inTheLastMonth || 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheNextMonth}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="No Próximo Mês ({count})"
                id="V0ZN2j"
                values={{
                  count: eventsCalendar?.agenda?.breakdown?.inTheNextMonth || 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheNextNinetyDays}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="Nos Próximos 90D ({count})"
                id="cBNpb1"
                values={{
                  count: eventsCalendar?.agenda?.breakdown?.inTheNextNinetyDays || 0,
                }}
              />
            </SelectItem>
            <SelectItem value={EventsCalendarFilterRange.InTheYear}>
              <FormattedMessage
                description="Página de eventos (Agenda) - Filtros"
                defaultMessage="Em 12 meses ({count})"
                id="qL53nf"
                values={{
                  count: eventsCalendar?.agenda?.breakdown?.inTheYear || 0,
                }}
              />
            </SelectItem>
            {period === EventsCalendarFilterRange.InSelectedPeriod && (
              <SelectItem value={EventsCalendarFilterRange.InSelectedPeriod}>
                <FormattedMessage
                  description="Página de eventos (Agenda) - Filtros"
                  defaultMessage="Período Selecionado ({totalEvents})"
                  id="3il2/4"
                  values={{
                    totalEvents: eventsCalendar?.agenda.totalCount ?? 0,
                  }}
                />
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      )}

      {/* TODO: For some reason this is throwing on the server side (useLayoutEffect) */}
      {enableFilters.includes(RightSideFiltersEnum.MonthViewCalendar) && (
        <TwoPassRender>
          {() => (
            <Calendar
              required={false}
              mode="range"
              captionLayout="label"
              numberOfMonths={1}
              defaultMonth={new Date()}
              startMonth={addMonths(new Date(), -6)}
              endMonth={addMonths(new Date(), 6)}
              month={month}
              selected={{
                from: calendarInterval.startDate,
                to: calendarInterval.endDate,
              }}
              onSelect={(range) => {
                startTransition(() => {
                  const from = range?.from ?? new Date();
                  setCalendarInterval({
                    startDate: from,
                    endDate: range?.to ?? from,
                  });
                  setPeriod(EventsCalendarFilterRange.InSelectedPeriod);
                });
              }}
              onMonthChange={(month) => {
                setMonth(month);
              }}
            />
          )}
        </TwoPassRender>
      )}

      <CalendarNotes />
    </div>
  );
}
